import * as THREE from 'three';
import { LEGACY_WORLD_LIGHT_DIRECTION } from './LegacyWorldLighting';

const WIDTH = 1024;
const HEIGHT = 512;
const HORIZON = [0.79, 0.87, 0.92] as const;
const horizonLinear = new THREE.Color(...HORIZON).convertSRGBToLinear();
export const CREATIVE_SKY_FOG_COLOR = [horizonLinear.r, horizonLinear.g, horizonLinear.b] as const;

/** A full spherical backdrop: no finite dome to fly through or texture downloads.
 * Clouds use direction-space noise so the longitude seam and poles stay continuous.
 * Generate once on entry; the ordinary Three background pass handles camera motion.
 */
export class CreativeSkybox {
  readonly texture: THREE.DataTexture;

  constructor() {
    const pixels = new Uint8Array(WIDTH * HEIGHT * 4);
    for (let row = 0; row < HEIGHT; row++) {
      // DataTexture has flipY=false: the first row is the nadir, the last the zenith.
      const latitude = (row / (HEIGHT - 1) - 0.5) * Math.PI;
      const y = Math.sin(latitude);
      const radius = Math.cos(latitude);
      for (let column = 0; column < WIDTH; column++) {
        const longitude = (column / (WIDTH - 1) - 0.5) * Math.PI * 2;
        const x = radius * Math.cos(longitude);
        const z = radius * Math.sin(longitude);
        const color = skyColor(x, y, z);
        const offset = (row * WIDTH + column) * 4;
        for (const channel of [0, 1, 2] as const) {
          pixels[offset + channel] = Math.round(255 * THREE.MathUtils.clamp(color[channel], 0, 1));
        }
        pixels[offset + 3] = 255;
      }
    }
    this.texture = new THREE.DataTexture(pixels, WIDTH, HEIGHT);
    this.texture.name = 'creative-island-skybox';
    this.texture.mapping = THREE.EquirectangularReflectionMapping;
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.wrapS = THREE.RepeatWrapping;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}

type RGB = readonly [number, number, number];
const smooth = THREE.MathUtils.smoothstep;

function mix(a: RGB, b: RGB, amount: number): RGB {
  return [
    a[0] + (b[0] - a[0]) * amount,
    a[1] + (b[1] - a[1]) * amount,
    a[2] + (b[2] - a[2]) * amount,
  ];
}

function skyColor(x: number, y: number, z: number): RGB {
  let color = mix(HORIZON, [0.24, 0.48, 0.79], Math.pow(Math.max(0, y), 0.55));
  if (y < 0) color = mix(HORIZON, [0.65, 0.78, 0.85], smooth(-y, 0, 0.65));

  const sunDot = Math.max(
    0,
    x * LEGACY_WORLD_LIGHT_DIRECTION[0] +
      y * LEGACY_WORLD_LIGHT_DIRECTION[1] +
      z * LEGACY_WORLD_LIGHT_DIRECTION[2],
  );
  color = mix(color, [1, 0.91, 0.72], Math.pow(sunDot, 18) * 0.28);
  color = mix(color, [1, 0.97, 0.84], Math.pow(sunDot, 350) * 0.45);
  color = mix(color, [1, 0.99, 0.93], smooth(sunDot, 0.99965, 0.99985));

  const cloudBand = smooth(y, 0.06, 0.22) * (1 - smooth(y, 0.88, 1));
  if (cloudBand > 0) {
    const density = cloudNoise(x * 6.5 + 13, y * 11 + 7, z * 6.5 - 5);
    const coverage = smooth(density, 0.48, 0.68) * cloudBand;
    const light = smooth(density, 0.49, 0.72);
    const clouds = mix([0.69, 0.77, 0.85], [1, 0.99, 0.96], light);
    color = mix(color, clouds, coverage * 0.94);
  }
  return color;
}

function cloudNoise(x: number, y: number, z: number): number {
  let result = 0;
  let amplitude = 0.54;
  for (let octave = 0; octave < 5; octave++) {
    result += noise(x, y, z) * amplitude;
    x = x * 2.03 + 11;
    y = y * 2.03 + 3;
    z = z * 2.03 + 7;
    amplitude *= 0.48;
  }
  return result;
}

function noise(x: number, y: number, z: number): number {
  const ix = Math.floor(x),
    iy = Math.floor(y),
    iz = Math.floor(z);
  const fx = smooth(x - ix, 0, 1),
    fy = smooth(y - iy, 0, 1),
    fz = smooth(z - iz, 0, 1);
  const lerp = THREE.MathUtils.lerp;
  return lerp(
    lerp(
      lerp(hash(ix, iy, iz), hash(ix + 1, iy, iz), fx),
      lerp(hash(ix, iy + 1, iz), hash(ix + 1, iy + 1, iz), fx),
      fy,
    ),
    lerp(
      lerp(hash(ix, iy, iz + 1), hash(ix + 1, iy, iz + 1), fx),
      lerp(hash(ix, iy + 1, iz + 1), hash(ix + 1, iy + 1, iz + 1), fx),
      fy,
    ),
    fz,
  );
}

function hash(x: number, y: number, z: number): number {
  let value = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 1274126177);
  value = Math.imul(value ^ (value >>> 13), 1274126177);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
}
